import React, { useState } from "react";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import VehicleDetails from "./components/vehicledetails";
import JourneyForm from "./components/journeyForm";
import PendingForm from "./components/pendingForm";
import EditStopMap from "./components/EditStopMap";
import StopSelector from "./components/StopSelector";
import StopForm from "./components/stopForm";
import { toast } from "react-toastify";
import { produce } from "immer";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";

// ---- AUTO-SORT ALGORITHM UTILS ---- //

const decodePolyline = (encoded) => {
  if (!encoded) return [];
  const points = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let b, shift = 0, result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    const dlat = result & 1 ? ~(result >> 1) : result >> 1; lat += dlat;
    shift = result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    const dlng = result & 1 ? ~(result >> 1) : result >> 1; lng += dlng;
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
};

// Ye stop ka distance root origin se calculate karega
const getDistanceAlongPath = (path, lat, lng) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const calcDist = (p1, p2) => {
    const R = 6371e3;
    const dLat = toRad(p2.lat - p1.lat);
    const dLng = toRad(p2.lng - p1.lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(p1.lat)) * Math.cos(toRad(p2.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  if (!path || path.length === 0) return 0;
  let minDistance = Infinity;
  let distanceAlongPath = 0;
  let accumulatedDistance = 0;

  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];

    const x1 = a.lng, y1 = a.lat, x2 = b.lng, y2 = b.lat, x0 = lng, y0 = lat;
    const dx = x2 - x1, dy = y2 - y1;
    let t = 0;
    if (dx !== 0 || dy !== 0) {
      t = ((x0 - x1) * dx + (y0 - y1) * dy) / (dx * dx + dy * dy);
      t = Math.max(0, Math.min(1, t));
    }

    const projLat = y1 + t * dy;
    const projLng = x1 + t * dx;

    const distToSegment = calcDist({ lat, lng }, { lat: projLat, lng: projLng });

    if (distToSegment < minDistance) {
      minDistance = distToSegment;
      distanceAlongPath = accumulatedDistance + calcDist(a, { lat: projLat, lng: projLng });
    }
    accumulatedDistance += calcDist(a, b);
  }
  return distanceAlongPath;
};

const reorderSequenceByRoute = (journey, encodedPolyline) => {
  // Agar polyline pass nahi hui toh fallback check karo
  const polylineStr = encodedPolyline || journey?.wayPointsPolyLine;

  if (!journey?.filters || !polylineStr) return journey;
  const path = decodePolyline(polylineStr);

  const filterEntries = Object.entries(journey.filters).filter(
    ([id, f]) => f?.data?.location
  );

  const stopsWithDistance = filterEntries.map(([id, f]) => {
    const loc = f.data.location;
    const dist = getDistanceAlongPath(path, loc.latitude, loc.longitude);
    return { id, dist };
  });

  // Stops ko origin distance ke hisaab se ascending order mein sort karo
  stopsWithDistance.sort((a, b) => a.dist - b.dist);

  const newSequence = {};
  stopsWithDistance.forEach((item, index) => {
    newSequence[index] = item.id;
  });

  return {
    ...journey,
    sequence: newSequence,
  };
};

// ---- MAIN COMPONENT ---- //

function Journey() {

  const { data: journeysData = {} } = useSelector((state) => state.journey || {});

  const externalHandles = useSelector((state) =>
    state.journey?.data?.journeys?.map((j) => j.externalHandle)
  );

  // console.log("externalHandles:", externalHandles);

  const [selectedJourney, setSelectedJourney] = useState(null);
  const [tempJourney, setTempJourney] = useState(null);
  const [isPendingEdit, setIsPendingEdit] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);
  const [routeRange, setRouteRange] = useState([0, 100]);
  const [hoveredStopId, setHoveredStopId] = useState(null);

  // ... handleEditJourney, handleClearJourney, handleEditStops, handleBackToJourneyForm waise hi rahenge ...

  const handleEditJourney = (journey, temp = null) => {
    setSelectedJourney(journey);
    setTempJourney(temp);
    setIsPendingEdit(!!temp);
    setIsEditMode(false);
    setSelectedStop(null);
    setRouteRange([0, 100]); // Reset slider
  };

  const handleClearJourney = () => {
    setSelectedJourney(null);
    setTempJourney(null);
    setIsPendingEdit(false);
    setIsEditMode(false);
    setSelectedStop(null);
  };

  const handleEditStops = (tempJourneyOverride = null) => {
    if (!selectedJourney) {
      toast.error("Please select a journey first");
      return;
    }
    setIsEditMode(true);
    setSelectedStop(null);
    const journeyToUse = tempJourneyOverride || selectedJourney;
    const hasStops = journeyToUse.filters && Object.keys(journeyToUse.filters).length > 0;
    if (!hasStops) {
      toast.info("No stops yet – right-click on the map to add one.");
    }
    if (tempJourneyOverride) {
      setSelectedJourney({
        ...tempJourneyOverride,
        isTemp: true
      });
    }
    else {
      setIsEditMode(true);
    }
  };

  const handleBackToJourneyForm = () => {
    setIsEditMode(false);
    setSelectedStop(null);
  };

  const handleSelectNewStop = (newStop) => setSelectedStop(newStop);

  // UPDATED: Jab stop drag hokar update ho
  const handleStopUpdate = (filterId, lat, lng, freshPolyline) => {
    setSelectedJourney((prev) => {
      const updated = produce(prev, (draft) => {
        const loc = draft?.filters?.[filterId]?.data?.location;
        if (loc) {
          loc.latitude = lat;
          loc.longitude = lng;
        }

        // Nayi line aayi hai toh usko permanent local state me save kar do!
        if (freshPolyline && !draft.wayPointsPolyLine) {
          draft.wayPointsPolyLine = freshPolyline;
        }
      });

      // Ab stops line ke hisaab se order ho jayenge
      const polylineToUse = freshPolyline || updated.wayPointsPolyLine || tempJourney?.wayPointsPolyLine;
      return reorderSequenceByRoute(updated, polylineToUse);
    });
  };

  // UPDATED: Jab form save ho (Naya stop ya edit kiya hua stop)
  const handleJourneyUpdated = (updatedJourney) => {
    // Agar updatedJourney pass nahi hua hai (jaise Bulk Upload ke time)
    if (!updatedJourney) {
      toast.info("Stops uploaded! Please sync or refresh to see the new route.");

      // Agar aapke paas index.js me dispatch aur selectedVehicle available hai,
      // toh aap ye line un-comment kar sakte hain map refresh karne ke liye:
      // dispatch(fetchJourneys(selectedVehicle)); 

      return; // Code yahi ruk jayega, aage crash nahi karega
    }

    // Purana logic (jab direct edit se object aata hai)
    const polyline = updatedJourney.wayPointsPolyLine || tempJourney?.wayPointsPolyLine;
    const sortedJourney = reorderSequenceByRoute(updatedJourney, polyline);
    setSelectedJourney(sortedJourney);
    toast.info("Journey updated Successfully");
  };

  const handleImportStops = (sourceJourney) => {
    if (!selectedJourney || !sourceJourney) return;

    setSelectedJourney((prev) => {
      // Immer (produce) ka use karke state update
      const updated = produce(prev, (draft) => {
        draft.filters = sourceJourney.filters || {};
        draft.stops = sourceJourney.stops || {};
        draft.sequence = sourceJourney.sequence || {};

        // Polyline check: Taaki import ke baad stops route ke hisaab se sort ho jayein
        const polyline = draft.wayPointsPolyLine || tempJourney?.wayPointsPolyLine;
        if (polyline) {
          const sorted = reorderSequenceByRoute(draft, polyline);
          draft.sequence = sorted.sequence;
        }
      });
      return updated;
    });
    toast.success(`Stops imported from ${sourceJourney.journeyName}`);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox sx={{
        // height: '670px',
        height: "clamp(400px, 100vh, calc(100vh - 65px))",
        display: "flex", flexDirection: { xs: "column", md: "row" }, overflow: 'hidden'
      }}>
        {!isEditMode && (
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 1, width: "100%", height: "100%" }}>
            <Box sx={{ flex: { xs: "1", md: "0 0 500px" }, minWidth: 0 }}>
              <VehicleDetails onEditJourney={handleEditJourney} onClearJourney={handleClearJourney} externalHandles={externalHandles} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
              {isPendingEdit ? (
                <PendingForm selectedJourney={selectedJourney} tempJourney={tempJourney} onClearJourney={handleClearJourney} onEditStops={handleEditStops} />
              ) : (
                <JourneyForm selectedJourney={selectedJourney} onClearJourney={handleClearJourney} onEditStops={() => handleEditStops()} />
              )}
            </Box>
          </Box>
        )}

        {isEditMode && selectedJourney && (
          <>
            <StopSelector selectedJourney={selectedJourney} onClearJourney={handleClearJourney} onBack={handleBackToJourneyForm} onEditStop={(stop) => setSelectedStop(stop)} onJourneyUpdated={handleJourneyUpdated} routeRange={routeRange}
              setRouteRange={setRouteRange}
              onImportStops={handleImportStops}
              allJourneys={journeysData.journeys || []}
              onHoverStop={setHoveredStopId}
            />
            {selectedStop ? (
              <StopForm stop={selectedStop} onBack={() => setSelectedStop(null)} selectedJourney={selectedJourney} onJourneyUpdated={handleJourneyUpdated} />
            ) : (
              <EditStopMap selectedJourney={selectedJourney} onClearJourney={handleClearJourney} onBack={handleBackToJourneyForm} onSelectNewStop={handleSelectNewStop} onStopUpdate={handleStopUpdate} routeRange={routeRange} tempJourney={tempJourney} onJourneyUpdated={handleJourneyUpdated}
                hoveredStopId={hoveredStopId}
                key={`map-${Object.keys(selectedJourney.stops || {}).length}`}
              />
            )}
          </>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default Journey;